import { useEffect } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { IncidentQuery } from '@/graphql/generated/operations'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeTraceLogDrawer,
  selectIncidentTraceLogDrawerState,
} from '@/redux/ui/uiSlice'

import { shouldShowTraceLogs } from '../utils/feedUtils'

interface UseCloseTraceLogOnIneligibleIncidentIProps {
  incident: IncidentQuery['incident'] | undefined
}

/**
 * Closes the trace log drawer when the incident in view changes to one that
 * can't show trace logs (ENG-32091). The drawer's open state lives in
 * persisted redux, so without this it survives prev/next navigation from an
 * ACS incident onto e.g. a manual incident with no device, leaving the drawer
 * open on content that has no meaning for the incident on screen.
 */
export const useCloseTraceLogOnIneligibleIncident = ({
  incident,
}: UseCloseTraceLogOnIneligibleIncidentIProps): void => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIncidentTraceLogDrawerState)?.isOpen
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )

  // Flag-aware so the effect only tracks the slug when the gate actually
  // reads it — with the flag off, a slug-only polling backfill must not
  // re-run the check (it could close a deep-linked drawer, see below).
  const eligibilitySlug = isIncidentTypeDefinition
    ? incident?.incidentType?.slug
    : null

  useEffect(() => {
    // `incident` is undefined while a switch is in flight (the incident
    // drawer's stale-incident guard) — wait for the loaded incident.
    if (!incident?.id) {
      return
    }
    if (isOpen && !shouldShowTraceLogs(incident.type, eligibilitySlug)) {
      dispatch(closeTraceLogDrawer())
    }
    // Keyed on the loaded incident's identity and eligibility inputs — the
    // drawer polls every 3s, so `type`/`incidentType.slug` can change (e.g. a
    // backfilled ITD slug) without an id change. `isOpen` is intentionally
    // excluded: re-running when it flips would immediately close a drawer
    // just opened for the *current* incident — the Reasoning Trail device
    // deep-link opens it without passing the type gate and must stay open.
  }, [incident?.id, incident?.type, eligibilitySlug])
}
